import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { CrmPromoIdsMap, EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, MarketSliderConfiguration, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { MarketType } from '@frontend/sports/types/components/content';
import { RxFor } from '@rx-angular/template/for';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';

import { BadgeComponent } from '../crm-promotion/badge.component';
import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketSliderComponent } from './component/market-slider/market-slider.component';
import { MarketUtilsService } from './component/market-slider/service/market-slider-utils.service';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';

@Component({
    selector: 'ms-over-under-option-group',
    templateUrl: './over-under-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './over-under-option-group.component.scss',
    imports: [
        RxFor,
        NgClass,
        AsyncPipe,
        NgTemplateOutlet,
        MarketHelpComponent,
        BadgeComponent,
        OptionComponent,
        OptionPanelBottomActionComponent,
        MarketSliderComponent,
    ],
})
export class OverUnderOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    optionGroup = input<EventOptionGroup | null>(null);
    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    private optionGroupTrackingService = inject(OptionGroupTrackingService);
    private readonly optionGroupService = inject(OptionGroupService);
    private readonly marketGroupingConfig = inject(MarketGroupingConfig);
    private readonly marketSliderConfig = inject(MarketSliderConfiguration);
    private readonly marketUtilsService = inject(MarketUtilsService);
    readonly sitecore = inject(Sitecore);

    displayLineValueInSquares = this.marketGroupingConfig.shouldShowLineValueInSquares;

    readonly enableForTotal = computed(() => {
        const group = this.optionGroup();
        const isTotals = group?.detailedGrouping?.name === MarketType.Totals;
        const validGroup = !!group?.options?.length;
        return validGroup && isTotals && !this.isInSheetView && this.getSliderStatus();
    });

    sliderSteps: number[] = [];
    centerValue: number = 0;
    disableSlider: boolean = false;
    isSliderVisibleWithoutShowMore = false;

    private optionGroups$ = new BehaviorSubject<OptionGroup[]>([]);
    private visibleOptionsLimit$ = new BehaviorSubject<number | undefined>(undefined);
    currentValue$ = new BehaviorSubject<number>(0);

    readonly visibleOptionGroups$: Observable<OptionGroup[]> = combineLatest([
        this.optionGroups$,
        this.visibleOptionsLimit$,
        this.currentValue$,
    ]).pipe(
        map(([optionGroups, visibleOptionsLimit, selectedValue]) => {
            if (!this.isSliderVisibleWithoutShowMore) {
                if (!this.enableForTotal() || this.expanded) {
                    return visibleOptionsLimit != null ? optionGroups.filter((g, i) => i < visibleOptionsLimit) : optionGroups;
                }
            }
            return this.filterOptionGroupsBySelectedValue(optionGroups, selectedValue);
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );

    visibleOptionGroups = toSignal(
        this.visibleOptionGroups$.pipe(
            map((o) => o.length),
            distinctUntilChanged(),
        ),
        { initialValue: 0 },
    );

    expanded = false;
    showMoreLessButtonVisible = false;
    showMoreData: ShowMoreData;

    ngOnChanges(changes: SimpleChanges): void {
        const _optionGroup = this.optionGroup();
        if (changes.optionGroup && _optionGroup !== null) {
            const optionGroups = this.optionGroupService.getOptionGroups(_optionGroup, (option) => option.marketAttribute);
            this.optionGroups$.next(optionGroups);

            const shouldEnableSlider = _optionGroup.detailedGrouping?.name === MarketType.Totals && !this.isInSheetView && this.getSliderStatus();
            this.isSliderVisibleWithoutShowMore =
                optionGroups.length - 1 < this.marketGroupingConfig.overUnderGroupMaxNumberOfLines && shouldEnableSlider;
            if (shouldEnableSlider) {
                this.sliderSteps = this.marketUtilsService.getSteps(_optionGroup);
                this.centerValue = this.marketUtilsService.getBalanceLine(_optionGroup);
                this.disableSlider = !_optionGroup.online;
            } else {
                this.sliderSteps = [];
                this.centerValue = 0;
                this.disableSlider = true;
                this.currentValue$.next(0);
            }
        }

        if (changes.optionsLimitOverride) {
            this.calculateMaxNumberOfVisibleOptions();
        }

        if (changes.optionsLimitOverride || changes.optionGroup) {
            const totalOptions = this.optionGroups$.value.length;
            this.showMoreLessButtonVisible = totalOptions > (this.optionsLimitOverride ?? this.marketGroupingConfig.overUnderGroupMaxNumberOfLines);

            this.showMoreData = {
                totalCount: totalOptions,
                initialVisibleCount: this.optionsLimitOverride ?? this.marketGroupingConfig.overUnderGroupMaxNumberOfLines,
                pageSize: totalOptions,
            };
        }
    }

    trackGroup(index: number, group: OptionGroup): string {
        return group.attribute || '';
    }

    isMarketPromoted(optionGroupId: number, templateId: number): boolean {
        return !!this.crmPromoIdsMap.markets[optionGroupId] || (templateId != null && !!this.crmPromoIdsMap.templateIds[templateId]);
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded = state !== 'collapsed';
        this.calculateMaxNumberOfVisibleOptions();

        this.trackExpandCollapse();
    }

    onShowMoreLessButtonToggle(): void {
        this.expanded = !this.expanded;

        this.trackExpandCollapse();

        this.calculateMaxNumberOfVisibleOptions();
    }

    setSliderCurrentValue(value: number): void {
        this.currentValue$.next(value);
    }

    private calculateMaxNumberOfVisibleOptions(): void {
        this.visibleOptionsLimit$.next(
            this.isInSheetView || this.expanded ? undefined : (this.optionsLimitOverride ?? this.marketGroupingConfig.overUnderGroupMaxNumberOfLines),
        );
    }

    private trackExpandCollapse() {
        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            this.expanded ? 'expand' : 'collapse',
            this.event.participants,
            this.optionGroup.name,
            this.optionGroup.name,
        );
    }

    private getSliderStatus(): boolean {
        const group = this.optionGroup();
        return !!group && this.marketSliderConfig.enableForTotal && this.marketSliderConfig.activationThreshold <= group.options.length;
    }

    private filterOptionGroupsBySelectedValue(optionGroups: OptionGroup[], selectedValue: number): OptionGroup[] {
        const selectedValueOnSlider = new Set<string>();
        const absSelectedValue = Math.abs(selectedValue);

        return optionGroups.reduce((uniqueGroups: OptionGroup[], group) => {
            const filteredOptions = group.options?.filter((option) => Math.abs(Number(option?.marketAttribute)) === absSelectedValue);

            if (filteredOptions?.length) {
                const key = Math.abs(Number(filteredOptions[0]?.optionAttribute)).toString();
                if (!selectedValueOnSlider.has(key)) {
                    selectedValueOnSlider.add(key);
                    uniqueGroups.push({ ...group, options: filteredOptions });
                }
            }

            return uniqueGroups;
        }, []);
    }
}
